import { useState } from 'react';

type Section = 'about' | 'services' | 'contact' | 'hero';

const contentData: Record<Section, { title: string; fields: { label: string; value: string; type: 'text' | 'textarea' }[] }> = {
  about: {
    title: 'About Page Content',
    fields: [
      { label: 'Hero Heading', value: 'We Build Technology That Moves Businesses Forward.', type: 'text' },
      { label: 'Hero Subtitle', value: 'FormaCode Technologies is a modern digital studio blending engineering, AI and design to deliver products that scale.', type: 'textarea' },
      { label: 'Mission', value: 'To empower businesses with intelligent digital solutions that reduce costs, accelerate growth, and create competitive advantage through technology.', type: 'textarea' },
      { label: 'Vision', value: 'A world where every business — regardless of size — has access to enterprise-grade technology, AI automation, and digital experiences that rival the world\'s best.', type: 'textarea' },
    ],
  },
  services: {
    title: 'Services Page Content',
    fields: [
      { label: 'Hero Heading', value: 'Your Digital Growth Stack', type: 'text' },
      { label: 'Hero Subtitle', value: 'End-to-end technology services — from building your website to automating your operations and driving growth.', type: 'textarea' },
      { label: 'Process Title', value: 'Our Process', type: 'text' },
      { label: 'Process Subtitle', value: 'A battle-tested workflow refined across 120+ projects. Transparent, iterative, and built for results.', type: 'textarea' },
    ],
  },
  contact: {
    title: 'Contact Page Content',
    fields: [
      { label: 'Hero Heading', value: 'Start Your Project', type: 'text' },
      { label: 'Hero Subtitle', value: 'Tell us what you are building. We will get back within 24 hours with a clear plan and quote.', type: 'textarea' },
      { label: 'Email Address', value: 'info@formacodetech.com', type: 'text' },
      { label: 'WhatsApp Number', value: '03706620393', type: 'text' },
    ],
  },
  hero: {
    title: 'Home Page Hero',
    fields: [
      { label: 'Main Heading', value: 'Build. Automate. Scale.', type: 'text' },
      { label: 'Subtitle', value: 'Intelligent digital products, AI automation, and growth strategies for modern businesses.', type: 'textarea' },
      { label: 'CTA Primary', value: 'Start Your Project', type: 'text' },
      { label: 'CTA Secondary', value: 'Explore Services', type: 'text' },
    ],
  },
};

export default function AdminContent() {
  const [activeSection, setActiveSection] = useState<Section>('about');
  const [editing, setEditing] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem('fc_content_edits');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    try {
      localStorage.setItem('fc_content_edits', JSON.stringify(editing));
    } catch {
      /* ignore */
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setEditing({});
    try {
      localStorage.removeItem('fc_content_edits');
    } catch {
      /* ignore */
    }
  };

  const current = contentData[activeSection];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Content Editor</h1>
          <p className="text-sm text-white/50 mt-1">Edit website content, headings, and contact information.</p>
        </div>
        {saved && (
          <div className="text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-lg border border-emerald-500/20">
            <i className="ri-check-line mr-1" /> Changes saved locally!
          </div>
        )}
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2">
        {(['about', 'services', 'contact', 'hero'] as Section[]).map((s) => (
          <button
            key={s}
            onClick={() => setActiveSection(s)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition whitespace-nowrap ${
              activeSection === s
                ? 'bg-neon-500/10 text-neon-300 border border-neon-500/20'
                : 'glass text-white/60 border border-white/5 hover:border-white/20 hover:text-white'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Content Form */}
      <div className="glass rounded-xl border border-white/5 p-6">
        <h2 className="font-display font-semibold text-lg text-white mb-6">{current.title}</h2>
        <div className="space-y-5">
          {current.fields.map((field) => (
            <div key={field.label}>
              <label className="block text-xs font-semibold text-white/70 mb-2">{field.label}</label>
              {field.type === 'textarea' ? (
                <textarea
                  defaultValue={field.value}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition resize-none"
                  onChange={(e) => setEditing({ ...editing, [field.label]: e.target.value })}
                />
              ) : (
                <input
                  type="text"
                  defaultValue={field.value}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                  onChange={(e) => setEditing({ ...editing, [field.label]: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={handleSave} className="btn-primary text-white font-semibold px-6 py-2.5 rounded-md text-sm whitespace-nowrap inline-flex items-center gap-2">
            <i className="ri-save-line" /> Save Changes
          </button>
          <button onClick={handleReset} className="px-6 py-2.5 rounded-md text-sm font-semibold border border-white/10 text-white/70 hover:bg-white/5 transition whitespace-nowrap">
            Reset
          </button>
        </div>
      </div>

      <div className="glass rounded-xl border border-white/5 p-6">
        <h2 className="font-display font-semibold text-lg text-white mb-4">Content Preview</h2>
        <div className="p-4 rounded-lg bg-white/5 border border-white/5">
          <div className="text-xs font-mono text-neon-400 mb-2">// {activeSection.toUpperCase()}</div>
          {current.fields.map((field) => (
            <div key={field.label} className="mb-3 last:mb-0">
              <div className="text-xs text-white/40 mb-1">{field.label}</div>
              <div className="text-sm text-white/80">{editing?.[field.label] || field.value}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-white/40">
          <i className="ri-information-line mr-1" /> Full content editing with database persistence will be available after backend connection.
        </div>
      </div>
    </div>
  );
}